import store from '@store/index';
import { appName } from '@utils/localStorageHelpers';
import Mixpanel from 'mixpanel-browser';
import { mixpanelEvents } from './mixpanelEvent';
// AuthContext only calls Mixpanel.init() when a MIXPANEL_TOKEN is configured (skipped entirely
// otherwise, e.g. local/CI environments with no token) - Mixpanel.track() throws
// "Cannot read properties of undefined (reading 'before_track')" if called before init(), since
// init() is what sets up the instance's internal hooks object. setMixpanelInitialized(true) is
// called right alongside that Mixpanel.init() call so every trackEvent() call site (all of them
// route through here) can no-op safely instead of crashing the app with an uncaught exception.
let mixpanelInitialized = false;
export const setMixpanelInitialized = (initialized) => {
    mixpanelInitialized = initialized;
};
export const trackEvent = ({ event, options, preAuth }) => {
    if (!mixpanelInitialized)
        return;
    const state = store.getState();
    const user = state?.auth?.user;
    if (!user?.tenant_id && !preAuth)
        return;
    const defaultOptions = {
        Application: appName,
        'Application Version': process.env.version,
        'Application Environment': process.env.APP_ENV,
        'User Id': user?.user_id ? Number(user?.user_id) : undefined,
        'Organization Id': user?.tenant_id ? Number(user?.tenant_id) : undefined,
        ...options,
    };
    Mixpanel.track(event, { ...defaultOptions });
};
export const socialLoginInitiatedEvent = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOGIN_SOCIAL_MEDIA_INITIATED,
        options,
        preAuth: true,
    });
};
export const socialLoginSuccessEvent = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOGIN_SOCIAL_MEDIA_SUCCESS,
        options,
        preAuth: true,
    });
};
export const candidateCustomInstructionsViewed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_CUSTOM_INSTRUCTIONS_VIEWED,
        options,
    });
};
export const candidateCustomInstructionsContinueClicked = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_CUSTOM_INSTRUCTIONS_CONTINUE_CLICKED,
        options,
    });
};
export const candidatePostCustomInstructionsViewed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_POST_CUSTOM_INSTRUCTIONS_VIEWED,
        options,
    });
};
export const candidatePostCustomInstructionsContinueClicked = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_POST_CUSTOM_INSTRUCTIONS_CONTINUE_CLICKED,
        options,
    });
};
export const instructionPageLandedEvent = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_INSTRUCTION_PAGE_LANDED,
        options,
    });
};
export const candidateInstructionWalkthroughStartedWithoutSkip = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_INSTRUCTION_WALKTHROUGH_STARTED_WITHOUT_SKIP,
        options,
    });
};
export const candidateInstructionWalkthroughStartedWithSkip = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_INSTRUCTION_WALKTHROUGH_STARTED_WITH_SKIP,
        options,
    });
};
export const candidateViewExtendedDuration = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_VIEW_EXTENDED_DURATION,
        options,
    });
};
export const cxStepWindowEarlyJoinBlocked = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_EARLY_JOIN_BLOCKED,
        options,
    });
};
export const cxStepWindowOnboardingAllowed = () => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_ONBOARDING_ALLOWED,
        options: {},
    });
};
export const cxStepWindowActionWaitingScreenShown = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_ACTION_WAITING_SCREEN_SHOWN,
        options,
    });
};
export const cxStepWindowAutoRedirectedToExam = () => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_AUTO_REDIRECTED_TO_EXAM,
        options: {},
    });
};
export const cxStepWindowActionAllowed = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_ACTION_ALLOWED,
        options,
    });
};
export const cxStepWindowOnboardingFallbackToAction = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_ONBOARDING_FALLBACK_TO_ACTION,
        options,
    });
};
export const cxStepWindowLateOnboardingAllowed = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_LATE_ONBOARDING_ALLOWED,
        options,
    });
};
export const cxStepWindowApiError = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_API_ERROR,
        options,
    });
};
export const cxStepWindowNoRestrictionFlow = () => {
    trackEvent({
        event: mixpanelEvents.CX_STEP_WINDOW_NO_RESTRICTION_FLOW,
        options: {},
    });
};
export const bulkAnswerSubmitted = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASSESSMENT_SUBMITTED,
        options,
    });
};
export const candidateAnswerSelected = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ANSWER_SELECTED,
        options,
    });
};
export const candidateNextQuestionClicked = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_NEXT_QUESTION_CLICKED,
        options,
    });
};
export const assessmentCandidateReportQuestionEvent = (options) => {
    trackEvent({
        event: mixpanelEvents.ASSESSMENT_CANDIDATE_REPORT_QUESTION,
        options,
    });
};
export const candidateLoginOtpRequested = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOGIN_OTP_REQUESTED,
        options,
        preAuth: true,
    });
};
export const candidateLoginSocialMediaLoginFailed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOGIN_SOCIAL_MEDIA_LOGIN_FAILED,
        options,
        preAuth: true,
    });
};
export const candidateLoginOtpFilledLoginSuccess = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOGIN_OTP_FILLED_LOGIN_SUCCESS,
        options,
    });
};
export const candidateScorePageNextClicked = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SCORE_PAGE_NEXT_CLICKED,
        options,
    });
};
export const taskTakerFeedbackPage = (options) => {
    trackEvent({
        event: mixpanelEvents.TASK_TAKER_FEEDBACK_PAGE,
        options,
    });
};
export const candidateScorePageLoad = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SCORE_PAGE_LOAD,
        options,
    });
};
export const ivyReportConfigVisualCuesDisabled = (options) => {
    trackEvent({
        event: mixpanelEvents.IVY_REPORT_CONFIG_VISUAL_CUES_DISABLED,
        options,
    });
};
export const ivyReportConfigScoringDisabled = (options) => {
    trackEvent({
        event: mixpanelEvents.IVY_REPORT_CONFIG_SCORES_DISABLED,
        options,
    });
};
export const cxMcqFilterPanelOpened = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_MCQ_FILTER_PANEL_OPENED,
        options,
    });
};
export const cxMcqFilterPanelClosed = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_MCQ_FILTER_PANEL_CLOSED,
        options,
    });
};
export const cxMcqFilterApplied = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_MCQ_FILTER_APPLIED,
        options,
    });
};
export const cxMcqFilterCleared = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_MCQ_FILTER_CLEARED,
        options,
    });
};
export const candidateAuthenticated = () => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_AUTHENTICATED,
        options: {},
    });
};
export const cxIvyAudioNoiseCancellationEnabled = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_IVY_AUDIO_NOISE_CANCELLATION_ENABLED,
        options,
    });
};
export const cxIvyAudioNoiseCancellationFailed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_IVY_AUDIO_NOISE_CANCELLATION_FAILED,
        options,
    });
};
export const cxAsyncSkipCtaClicked = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_SKIP_CTA_CLICKED,
        options,
    });
};
export const cxAsyncSkipModalViewed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_SKIP_MODAL_VIEWED,
        options,
    });
};
export const cxAsyncSkipModalCancelled = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_SKIP_MODAL_CANCELLED,
        options,
    });
};
export const cxAsyncSkipConfirmed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_SKIP_CONFIRMED,
        options,
    });
};
export const cxAsyncAssessmentResumeAttempted = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_ASSESSMENT_RESUME_ATTEMPTED,
        options,
    });
};
export const cxAsyncAssessmentResumed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_ASSESSMENT_RESUMED,
        options,
    });
};
export const cxAsyncAssessmentResumeFailed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_ASSESSMENT_RESUME_FAILED,
        options,
    });
};
export const cxAsyncTimerResumed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_ASYNC_TIMER_RESUMED,
        options,
    });
};
export const cxIvyParticipantLeft = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_IVY_PARTICIPANT_LEFT,
        options,
    });
};
export const cxIvyParticipantJoined = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_IVY_PARTICIPANT_JOINED,
        options,
    });
};
export const stopRecordingClick = () => {
    trackEvent({
        event: mixpanelEvents.STOP_RECORDING_CLICK,
        options: {},
    });
};
export const nextAsyncQuestionClick = () => {
    trackEvent({
        event: mixpanelEvents.NEXT_ASYNC_QUESTION_CLICK,
        options: {},
    });
};
export const candidateAsyncPermissionAllowed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASYNC_PERMISSION_ALLOWED,
        options,
    });
};
export const candidateAsyncTogglePreviewClick = (hide) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASYNC_TOGGLE_PREVIEW_CLICK,
        options: { 'Preview Status': hide ? 'hidden' : 'visible' },
    });
};
export const candidateAsyncPracticeScreenViewed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASYNC_PRACTICE_SCREEN_VIEWED,
        options,
    });
};
export const candidateAsyncPermissionRequested = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASYNC_PERMISSION_REQUESTED,
        options,
    });
};
export const candidateAsyncDeviceAccessFailed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASYNC_DEVICE_ACCESS_FAILED,
        options,
    });
};
export const candidateAsyncHwCheckComplete = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASYNC_HW_CHECK_COMPLETE,
        options,
    });
};
export const candidatePaymentProceed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_PAYMENT_PROCEED,
        options,
    });
};
export const candidateCouponCodeApply = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_COUPON_CODE_APPLY,
        options,
    });
};
export const candidatePayBookPageScheduleNow = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_PAY_BOOK_PAGE_SCHEDULE_NOW,
        options,
    });
};
export const candidateScheduleClick = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SCHEDULE_CLICK,
        options,
    });
};
export const candidateLoginClick = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOGIN_CLICK,
        options,
    });
};
export const candidateSlotBookingConfirm = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SLOT_BOOKING_CONFIRM,
        options,
    });
};
export const candidateRegistrationContinue = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_REGISTRATION_CONTINUE,
        options,
    });
};
export const candidateClickGetStartedInstructions = (options) => {
    trackEvent({
        event: mixpanelEvents.INSTRUCTIONS_CANDIDATE_CLICK_GET_STARTED,
        options,
    });
};
export const candidateClickTermsAndConditionsInstructionsPage = (options) => {
    trackEvent({
        event: mixpanelEvents.INSTRUCTIONS_CANDIDATE_CLICK_TERMS_CONDITIONS,
        options,
    });
};
export const candidateClickPrivacyPolicyInstructionsPage = (options) => {
    trackEvent({
        event: mixpanelEvents.INSTRUCTIONS_CANDIDATE_CLICK_PRIVACY_POLICY,
        options,
    });
};
export const candidateLandsOnThankYouPage = (options) => {
    trackEvent({
        event: mixpanelEvents.FORM_CANDIDATE_LAND_THANK_YOU_PAGE,
        options,
    });
};
export const candidateLandsOnLobbyWaitPage = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOBBY_WAIT_PAGE_LANDED,
        options,
    });
};
export const candidateTransitionsFromLobbyToAlmostThere = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LOBBY_TO_ALMOST_THERE_TRANSITION,
        options,
    });
};
export const candidateTransitionsFromAlmostThereToReady = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ALMOST_THERE_TO_READY_TRANSITION,
        options,
    });
};
export const candidateLandsOnMissedSlotPage = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_MISSED_SLOT_PAGE_LANDED,
        options,
    });
};
export const candidateClicksBackToQueue = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_BACK_TO_QUEUE_CLICKED,
        options,
    });
};
export const candidateSlotBookingPageLanded = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SLOT_BOOKING_PAGE_LANDED,
        options,
    });
};
export const candidateSlotConfirmed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SLOT_CONFIRMED,
        options,
    });
};
export const candidateBookingConfirmRescheduleClick = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_BOOKING_CONFIRM_RESCHEDULE_CLICK,
        options,
    });
};
export const candidateLandsOnEarlyBirdPage = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_EARLY_BIRD_PAGE_LANDED,
        options,
    });
};
export const candidateAcceptProposedNewTime = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_ACCEPT_PROPOSED_TIME,
        options,
    });
};
export const candidateFeedbackSubmit = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FEEDBACK_SUBMIT,
        options,
    });
};
export const candidateInterviewEarlyBirdPage = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_INTERVIEW_EARLY_BIRD_PAGE_LANDED,
        options,
    });
};
export const candidateDeclineProposedNewTime = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_DECLINE_PROPOSED_TIME,
        options,
    });
};
export const candidateProposeNewTime = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_PROPOSE_ALTERNATIVE_TIME,
        options,
    });
};
export const candidateInterviewJoin = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_INTERVIEW_JOIN,
        options,
    });
};
export const candidateFeedbackSkip = () => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FEEDBACK_SKIP,
        options: {},
    });
};
export const proposeNewTimeAction = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_PROPOSE_TIME_LAND,
        options,
    });
};
export const proposeNewTimeActionConfirm = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_PROPOSE_TIME_CONFIRM,
        options,
    });
};
export const declineInterviewPageLand = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_DECLINE_LAND,
        options,
    });
};
export const proposeNewTimeClickFromDecline = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_DECLINE_FLOW_PROPOSE_TIME_CLICK,
        options,
    });
};
export const declineConfirmClick = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_DECLINE_CONFIRM,
        options,
    });
};
export const candidateProposeTimeFromDeclineConfirm = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_DECLINE_FLOW_PROPOSE_TIME_CONFIRM,
        options,
    });
};
export const candidateProposeTimeLandFromDecline = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_DECLINE_FLOW_PROPOSE_TIME_LAND,
        options,
    });
};
export const candidateRsvpConfirm = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RSVP_CONFIRM,
        options,
    });
};
export const candidateFaceAuthAllow = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FACEAUTH_ALLOW,
        options,
    });
};
export const candidateFaceAuthBlock = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FACEAUTH_BLOCK,
        options,
    });
};
export const candidateFaceAuthCapture = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FACEAUTH_CAPTURE,
        options,
    });
};
export const candidateFaceAuthRetake = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FACEAUTH_RETAKE,
        options,
    });
};
export const candidateFaceAuthSubmit = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FACEAUTH_SUBMIT,
        options,
    });
};
export const candidateFaceAuthNextStep = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_FACEAUTH_NEXTSTEP,
        options,
    });
};
export const candidateExitURLProceedClick = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_EXIT_URL_PROCEED_CLICK,
        options,
    });
};
export const candidateBookingConfirmRequestCancellation = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_BOOKING_CONFIRM_REQUEST_CANCELLATION,
        options,
    });
};
export const candidateBookPayProceed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_BOOK_PAY_PROCEED,
        options,
    });
};
export const candidateCancellationPageRescheduleClick = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_CANCELLATION_PAGE_RESCHEDULE_CLICK,
        options,
    });
};
export const candidateRescheduleFeeProceed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RESCHEDULE_FEE_PROCEED,
        options,
    });
};
export const candidateCancellationPageProceed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_CANCELLATION_PAGE_PROCEED,
        options,
    });
};
export const candidateInvoicePageLanded = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_INVOICE_PAGE_LANDED,
        options,
    });
};
export const candidatePaidNotBookedScheduleNow = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_PAID_NOT_BOOKED_SCHEDULE_NOW,
        options,
    });
};
export const candidatePayBookProceed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_PAY_BOOK_PROCEED,
        options,
    });
};
export const trackCandidateLongFormSubmit = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_LONG_FORM_SUBMIT,
        options,
    });
};
export const cxPdfDownloadClicked = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_PDF_DOWNLOAD_CLICKED,
        options,
    });
};
export const cxPdfDownloadFailed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CX_PDF_DOWNLOAD_FAILED,
        options,
    });
};
export const candidateBookPayCompleted = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_BOOK_PAY_COMPLETED,
        options,
    });
};
export const candidateRunningLatePageLanded = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RUNNING_LATE_PAGE_LANDED,
        options,
    });
};
export const candidateRunningLatePageConfirmed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_RUNNING_LATE_PAGE_CONFIRMED,
        options,
    });
};
export const candidateInstructionPageProceed = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_INSTRUCTION_PAGE_PROCEED,
        options,
    });
};
export const candidateAssessmentIframeLoaded = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASSESSMENT_IFRAME_LOADED,
        options,
    });
};
export const candidateAssessmentIframeLoadFailed = (options = {}) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_ASSESSMENT_IFRAME_LOAD_FAILED,
        options,
    });
};
export const userStandalonePaymentLanded = (options) => {
    trackEvent({
        event: mixpanelEvents.USER_STANDALONEPAYMENT_LANDED,
        options,
    });
};
export const userStandalonePaymentCouponCodeApplied = (options) => {
    trackEvent({
        event: mixpanelEvents.USER_STANDALONEPAYMENT_COUPONCODE_APPLIED,
        options,
    });
};
export const userStandalonePaymentProceed = (options) => {
    trackEvent({
        event: mixpanelEvents.USER_STANDALONEPAYMENT_PROCEED,
        options,
    });
};
export const userStandalonePaymentStatus = (options) => {
    trackEvent({
        event: mixpanelEvents.USER_STANDALONEPAYMENT_STATUS,
        options,
    });
};
export const candidateSlotBookingDateSelected = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SLOT_BOOKING_DATE_SELECTED,
        options,
    });
};
export const candidateSlotBookingTimeSelected = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_SLOT_BOOKING_TIME_SELECTED,
        options,
    });
};
export const candidateBookPayCompletedRefactor = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_BOOK_PAY_COMPLETED_REFACTOR,
        options,
    });
};
export const candidateBookPayProceedRefactor = (options) => {
    trackEvent({
        event: mixpanelEvents.CANDIDATE_BOOK_PAY_PROCEED_REFACTOR,
        options,
    });
};
export const liveInterviewAvailabilityViewed = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_LIVE_INTERVIEW_AVAILABILITY_VIEWED,
        options,
    });
};
export const liveInterviewAvailabilitySlotSelected = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_LIVE_INTERVIEW_AVAILABILITY_SLOT_SELECTED,
        options,
    });
};
export const liveInterviewAvailabilitySubmitted = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_LIVE_INTERVIEW_AVAILABILITY_SUBMITTED,
        options,
    });
};
export const liveInterviewAvailabilitySubmissionFailed = (options) => {
    trackEvent({
        event: mixpanelEvents.CX_LIVE_INTERVIEW_AVAILABILITY_SUBMISSION_FAILED,
        options,
    });
};
export default {
    bulkAnswerSubmitted,
    candidateAnswerSelected,
    candidateAsyncPermissionAllowed,
    candidateAsyncTogglePreviewClick,
    candidateAsyncPracticeScreenViewed,
    candidateAsyncPermissionRequested,
    candidateAsyncDeviceAccessFailed,
    candidateAsyncHwCheckComplete,
    candidateAuthenticated,
    candidateClickGetStartedInstructions,
    candidateClickPrivacyPolicyInstructionsPage,
    candidateClicksBackToQueue,
    candidateClickTermsAndConditionsInstructionsPage,
    candidateCouponCodeApply,
    candidateLandsOnEarlyBirdPage,
    candidateLandsOnLobbyWaitPage,
    candidateLandsOnMissedSlotPage,
    candidateLandsOnThankYouPage,
    candidateLoginClick,
    candidateLoginOtpFilledLoginSuccess,
    candidateLoginOtpRequested,
    candidateLoginSocialMediaLoginFailed,
    candidateNextQuestionClicked,
    candidatePaymentProceed,
    candidateRegistrationContinue,
    candidatePayBookPageScheduleNow,
    candidateScorePageLoad,
    candidateScorePageNextClicked,
    candidateSlotBookingConfirm,
    candidateSlotBookingPageLanded,
    candidateSlotConfirmed,
    candidateTransitionsFromAlmostThereToReady,
    candidateTransitionsFromLobbyToAlmostThere,
    ivyReportConfigScoringDisabled,
    ivyReportConfigVisualCuesDisabled,
    instructionPageLandedEvent,
    nextAsyncQuestionClick,
    socialLoginInitiatedEvent,
    socialLoginSuccessEvent,
    stopRecordingClick,
    taskTakerFeedbackPage,
    trackEvent,
    setMixpanelInitialized,
    candidateAcceptProposedNewTime,
    candidateDeclineProposedNewTime,
    candidateProposeNewTime,
    candidateInterviewJoin,
    candidateInterviewEarlyBirdPage,
    candidateFeedbackSkip,
    candidateFeedbackSubmit,
    proposeNewTimeActionConfirm,
    proposeNewTimeAction,
    declineInterviewPageLand,
    proposeNewTimeClickFromDecline,
    declineConfirmClick,
    candidateProposeTimeFromDeclineConfirm,
    candidateProposeTimeLandFromDecline,
    candidateRsvpConfirm,
    candidateFaceAuthAllow,
    candidateFaceAuthBlock,
    candidateFaceAuthCapture,
    candidateFaceAuthRetake,
    candidateFaceAuthSubmit,
    candidateFaceAuthNextStep,
    candidateBookingConfirmRescheduleClick,
    candidateRescheduleFeeProceed,
    candidateBookPayProceed,
    candidateExitURLProceedClick,
    candidateBookingConfirmRequestCancellation,
    candidateCancellationPageRescheduleClick,
    candidateCancellationPageProceed,
    candidateInvoicePageLanded,
    candidatePaidNotBookedScheduleNow,
    candidatePayBookProceed,
    trackCandidateLongFormSubmit,
    cxPdfDownloadClicked,
    cxPdfDownloadFailed,
    candidateBookPayCompleted,
    candidateBookPayCompletedRefactor,
    candidateRunningLatePageLanded,
    candidateRunningLatePageConfirmed,
    candidateInstructionPageProceed,
    candidateAssessmentIframeLoaded,
    candidateAssessmentIframeLoadFailed,
    cxStepWindowEarlyJoinBlocked,
    cxStepWindowOnboardingAllowed,
    cxStepWindowActionWaitingScreenShown,
    cxStepWindowAutoRedirectedToExam,
    cxStepWindowActionAllowed,
    cxStepWindowOnboardingFallbackToAction,
    cxStepWindowLateOnboardingAllowed,
    cxStepWindowApiError,
    cxStepWindowNoRestrictionFlow,
    cxIvyAudioNoiseCancellationEnabled,
    cxIvyAudioNoiseCancellationFailed,
    cxAsyncAssessmentResumeAttempted,
    cxAsyncAssessmentResumeFailed,
    cxAsyncAssessmentResumed,
    userStandalonePaymentLanded,
    userStandalonePaymentCouponCodeApplied,
    userStandalonePaymentProceed,
    userStandalonePaymentStatus,
    cxAsyncTimerResumed,
    candidateSlotBookingDateSelected,
    candidateSlotBookingTimeSelected,
    candidateBookPayProceedRefactor,
    liveInterviewAvailabilityViewed,
    liveInterviewAvailabilitySlotSelected,
    liveInterviewAvailabilitySubmitted,
    liveInterviewAvailabilitySubmissionFailed,
};
